import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowLeft, Check, ChevronRight, FileText, ScanLine, CalendarDays, Car, GraduationCap, Sparkles } from "lucide-react";
import { useAuth } from "../App";
import { getPlugin } from "../data/hostlandPlugins";
import { CheckinBetaBanner } from "../components/CheckinBetaBanner";

const ICONS = { FileText, ScanLine, CalendarDays, Car, GraduationCap, Sparkles };

const PluginLanding = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();
  const plugin = getPlugin(slug);

  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [slug]);
  useEffect(() => {
    // Pagina INTERNA: forziamo noindex sul meta reale (quello statico di index.html)
    const robots = document.querySelector('meta[name="robots"]');
    const prev = robots ? robots.getAttribute("content") : null;
    if (robots) robots.setAttribute("content", "noindex, nofollow");
    return () => { if (robots && prev) robots.setAttribute("content", prev); };
  }, []);
  useEffect(() => {
    if (!loading && !isAuthenticated) navigate("/login", { replace: true });
  }, [loading, isAuthenticated, navigate]);

  if (loading) return null;
  if (!plugin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6">
        <div className="text-center">
          <h1 className="text-2xl font-black text-zinc-900 mb-2">Strumento non trovato</h1>
          <Link to="/dashboard" className="inline-block mt-2 px-5 py-2.5 rounded-full bg-zinc-900 text-white text-sm font-bold">Torna alla dashboard</Link>
        </div>
      </div>
    );
  }

  const Icon = ICONS[plugin.icon] || Sparkles;
  const headerStyle = plugin.image
    ? { backgroundImage: `linear-gradient(135deg, rgba(0,0,0,.45), rgba(0,0,0,.25)), url(${plugin.image})`, backgroundSize: "cover", backgroundPosition: "center" }
    : { background: `linear-gradient(135deg, ${plugin.from}, ${plugin.to})` };

  return (
    <div className="min-h-screen bg-zinc-50">
      <Helmet>
        <title>{plugin.seo.title}</title>
        <meta name="description" content={plugin.seo.description} />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Top bar */}
      <div className="sticky top-0 z-20 bg-white/80 backdrop-blur border-b border-zinc-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-14 flex items-center">
          <button onClick={() => navigate(-1)} data-testid="plugin-back-btn" className="inline-flex items-center gap-2 text-sm font-bold text-zinc-600 hover:text-zinc-900 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Indietro
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Hero banner */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="relative rounded-3xl p-6 sm:p-9 text-white overflow-hidden" style={headerStyle} data-testid="plugin-hero">
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center shrink-0"><Icon className="w-6 h-6" strokeWidth={2.2} /></span>
              {plugin.badge && <span className="px-2.5 py-1 rounded-full bg-white/20 text-[11px] font-black uppercase tracking-wider">{plugin.badge}</span>}
            </div>
            <h1 className="text-2xl sm:text-4xl font-black tracking-tight leading-tight">{plugin.hero.title}</h1>
            <p className="mt-3 text-sm sm:text-base font-medium text-white/85 max-w-xl leading-relaxed">{plugin.hero.subtitle}</p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              {!plugin.beta && (
                <a href={plugin.pricing.ctaLink} target={plugin.pricing.ctaLink.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid="plugin-hero-cta" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-zinc-900 font-black text-sm hover:scale-[1.03] transition-transform">
                  {plugin.pricing.cta} <ChevronRight className="w-4 h-4" />
                </a>
              )}
              {plugin.beta && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 text-[12px] font-black uppercase tracking-wider"><Sparkles className="w-3.5 h-3.5" /> Beta test · su richiesta</span>
              )}
              <span className="text-sm font-black text-white/90">{plugin.price}</span>
            </div>
          </div>
        </motion.div>

        {/* Banner beta test — registra la tua email per provare le nuove funzionalità */}
        {plugin.beta && (
          <div className="mt-8 sm:mt-10" data-testid="plugin-beta-banner">
            <CheckinBetaBanner forceShow showEmail />
          </div>
        )}

        {/* Cos'è */}
        <section className="mt-8 sm:mt-10" data-testid="plugin-what">
          <h2 className="text-xl sm:text-2xl font-black text-zinc-900 tracking-tight mb-3">Cos'è {plugin.title}</h2>
          <p className="text-[15px] leading-relaxed text-zinc-600">{plugin.what}</p>
        </section>

        {/* Vantaggi */}
        <section className="mt-8 sm:mt-10" data-testid="plugin-benefits">
          <h2 className="text-xl sm:text-2xl font-black text-zinc-900 tracking-tight mb-4">Perché ti conviene</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            {plugin.benefits.map((b, i) => (
              <div key={i} className="bg-white border border-zinc-100 rounded-2xl p-4" data-testid={`plugin-benefit-${i}`}>
                <span className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ background: `${plugin.from}1a`, color: plugin.from }}><Check className="w-5 h-5" strokeWidth={2.6} /></span>
                <div className="text-sm font-black text-zinc-900 mb-1">{b.title}</div>
                <div className="text-[13px] text-zinc-500 leading-relaxed">{b.desc}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Prezzo */}
        <section className="mt-8 sm:mt-10" data-testid="plugin-pricing">
          <div className="rounded-3xl p-6 sm:p-8 text-white flex flex-col sm:flex-row sm:items-center gap-5" style={headerStyle}>
            <div className="relative z-10 flex-1">
              <div className="text-[11px] font-black uppercase tracking-wider text-white/70 mb-1">Prezzo</div>
              <div className="text-2xl sm:text-3xl font-black">{plugin.pricing.label}</div>
              <div className="text-sm text-white/80 mt-1.5">{plugin.pricing.detail}</div>
            </div>
            <a href={plugin.pricing.ctaLink} target={plugin.pricing.ctaLink.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid="plugin-pricing-cta" className="relative z-10 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-white text-zinc-900 font-black text-sm hover:scale-[1.03] transition-transform shrink-0" style={plugin.beta ? { display: "none" } : undefined}>
              {plugin.pricing.cta} <ChevronRight className="w-4 h-4" />
            </a>
            {plugin.beta && (
              <span className="relative z-10 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-white/15 text-white font-black text-sm shrink-0" data-testid="plugin-pricing-beta"><Sparkles className="w-4 h-4" /> Provalo in beta qui sopra</span>
            )}
          </div>
        </section>

        {/* FAQ */}
        {plugin.faqs?.length > 0 && (
          <section className="mt-8 sm:mt-10" data-testid="plugin-faqs">
            <h2 className="text-xl sm:text-2xl font-black text-zinc-900 tracking-tight mb-4">Domande frequenti</h2>
            <div className="space-y-3">
              {plugin.faqs.map((f, i) => (
                <div key={i} className="bg-white border border-zinc-100 rounded-2xl p-4" data-testid={`plugin-faq-${i}`}>
                  <div className="text-sm font-black text-zinc-900 mb-1.5">{f.q}</div>
                  <div className="text-[13px] text-zinc-500 leading-relaxed">{f.a}</div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default PluginLanding;
